/**
 * FilterBar globale della dashboard (cross-filtering PowerBI-style).
 *
 * Mostra:
 *  - Multi-select stato preventivo (Bozza/Inviato/Vinto/Perso)
 *  - Multi-select cliente (top + search)
 *  - Select stratigrafia
 *  - Select fornitore
 *  - Pill dei filtri attivi con × per rimuovere
 *  - Pulsante "Pulisci tutto"
 *
 * Tutti i widget della dashboard si ricalcolano in tempo reale.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Check, Filter, X, UserSquare2, Layers, Factory, Tag, Bookmark, Trash2, Save } from 'lucide-react';
import { useDashboardFilters } from '@/contexts/DashboardFiltersContext';
import { useSuppliersList } from '@/hooks/useSupplierManagement';
import { Input } from '@/components/ui/input';

interface DashboardFilterBarProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customersData: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  estimatesData: any[];
}

const STATUS_OPTIONS: { value: string; label: string; color: string }[] = [
  { value: 'draft', label: 'Bozza',   color: 'bg-slate-100 text-slate-700' },
  { value: 'sent',  label: 'Inviato', color: 'bg-amber-100 text-amber-800' },
  { value: 'won',   label: 'Vinto',   color: 'bg-emerald-100 text-emerald-800' },
  { value: 'lost',  label: 'Perso',   color: 'bg-red-100 text-red-800' },
];

const DashboardFilterBar: React.FC<DashboardFilterBarProps> = ({ customersData, estimatesData }) => {
  const {
    filters, hasActiveFilters, resetFilters,
    toggleCustomer, toggleStatus, setStratigraphy, setSupplier,
    presets, savePreset, applyPreset, deletePreset,
  } = useDashboardFilters();

  const [statusOpen, setStatusOpen] = useState(false);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [stratOpen, setStratOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  // Solo i clienti che hanno almeno un progetto associato (più rilevanti).
  const customers = useMemo(() => {
    const usedIds = new Set<string>();
    for (const e of estimatesData) {
      const id = e?.projects?.customer_id;
      if (id) usedIds.add(id);
    }
    return customersData.filter(c => usedIds.has(c.id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [customersData, estimatesData]);

  const stratigraphyNames = useMemo(() => {
    const set = new Set<string>();
    for (const e of estimatesData) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const stratis = (e?.estimate_stratigraphies as any[] | null) ?? [];
      for (const s of stratis) {
        set.add(s.name || 'Senza nome');
      }
    }
    return Array.from(set).sort();
  }, [estimatesData]);

  // I fornitori del filtro vengono dalla tabella `suppliers` (globali + dell'org),
  // non dagli estimates: vogliamo poter filtrare anche per fornitori non ancora
  // usati nei preventivi (es. Knauf/Fassa nel demo dataset hanno materiali ma
  // nessun preventivo li include — comunque devono essere selezionabili nel filtro).
  const { data: suppliersFromDb = [] } = useSuppliersList();
  const suppliers = useMemo(() => {
    return suppliersFromDb
      .filter(s => s.is_active !== false)
      .map(s => s.name)
      .sort((a, b) => a.localeCompare(b, 'it'));
  }, [suppliersFromDb]);

  const customerLabel = (id: string): string => {
    const c = customers.find(x => x.id === id);
    return c?.name || id.slice(0, 8);
  };

  return (
    <div className="rounded-lg border bg-card p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground mr-1">
          <Filter className="h-3.5 w-3.5" />
          Filtri
        </div>

        {/* STATUS */}
        <Popover open={statusOpen} onOpenChange={setStatusOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5">
              <Tag className="h-3.5 w-3.5" />
              Stato
              {filters.statuses.length > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                  {filters.statuses.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-1" align="start">
            {STATUS_OPTIONS.map(opt => {
              const active = filters.statuses.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleStatus(opt.value)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/50 text-left"
                >
                  <div className="w-4 h-4 border rounded flex items-center justify-center">
                    {active && <Check className="h-3 w-3" />}
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${opt.color}`}>{opt.label}</span>
                </button>
              );
            })}
          </PopoverContent>
        </Popover>

        {/* CLIENTE */}
        <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5">
              <UserSquare2 className="h-3.5 w-3.5" />
              Cliente
              {filters.customerIds.length > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                  {filters.customerIds.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <Command>
              <CommandInput placeholder="Cerca cliente…" />
              <CommandList>
                <CommandEmpty>Nessun cliente trovato.</CommandEmpty>
                <CommandGroup>
                  {customers.map(c => {
                    const active = filters.customerIds.includes(c.id);
                    return (
                      <CommandItem
                        key={c.id}
                        value={c.name || c.id}
                        onSelect={() => toggleCustomer(c.id)}
                        className="gap-2"
                      >
                        <div className="w-4 h-4 border rounded flex items-center justify-center shrink-0">
                          {active && <Check className="h-3 w-3" />}
                        </div>
                        <span className="flex-1 truncate">{c.name}</span>
                        {c.city && <span className="text-xs text-muted-foreground">{c.city}</span>}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* STRATIGRAFIA */}
        <Popover open={stratOpen} onOpenChange={setStratOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5">
              <Layers className="h-3.5 w-3.5" />
              Stratigrafia
              {filters.stratigraphyName && (
                <Badge variant="secondary" className="h-4 px-1 text-[10px]">1</Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <Command>
              <CommandInput placeholder="Cerca stratigrafia…" />
              <CommandList>
                <CommandEmpty>Nessuna stratigrafia trovata.</CommandEmpty>
                <CommandGroup>
                  {stratigraphyNames.map(name => {
                    const active = filters.stratigraphyName === name;
                    return (
                      <CommandItem
                        key={name}
                        value={name}
                        onSelect={() => { setStratigraphy(name); setStratOpen(false); }}
                        className="gap-2"
                      >
                        <div className="w-4 h-4 border rounded flex items-center justify-center shrink-0">
                          {active && <Check className="h-3 w-3" />}
                        </div>
                        <span className="truncate">{name}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* FORNITORE */}
        <Popover open={supplierOpen} onOpenChange={setSupplierOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5">
              <Factory className="h-3.5 w-3.5" />
              Fornitore
              {filters.supplier && (
                <Badge variant="secondary" className="h-4 px-1 text-[10px]">1</Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput placeholder="Cerca fornitore…" />
              <CommandList>
                <CommandEmpty>Nessun fornitore.</CommandEmpty>
                <CommandGroup>
                  {suppliers.map(s => {
                    const active = filters.supplier === s;
                    return (
                      <CommandItem
                        key={s}
                        value={s}
                        onSelect={() => { setSupplier(s); setSupplierOpen(false); }}
                        className="gap-2"
                      >
                        <div className="w-4 h-4 border rounded flex items-center justify-center shrink-0">
                          {active && <Check className="h-3 w-3" />}
                        </div>
                        <span className="truncate">{s}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* PRESET */}
        <Popover open={presetOpen} onOpenChange={setPresetOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5 ml-auto">
              <Bookmark className="h-3.5 w-3.5" />
              Preset
              {presets.length > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-[10px]">{presets.length}</Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3 space-y-3" align="end">
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-muted-foreground">Salva combinazione corrente</div>
              <div className="flex gap-1.5">
                <Input
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="Nome preset…"
                  className="h-8 text-xs"
                  disabled={!hasActiveFilters}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && presetName.trim() && hasActiveFilters) {
                      savePreset(presetName);
                      setPresetName('');
                    }
                  }}
                />
                <Button
                  size="sm"
                  className="h-8 px-2"
                  disabled={!presetName.trim() || !hasActiveFilters}
                  onClick={() => {
                    savePreset(presetName);
                    setPresetName('');
                  }}
                >
                  <Save className="h-3.5 w-3.5" />
                </Button>
              </div>
              {!hasActiveFilters && (
                <p className="text-[10px] text-muted-foreground italic">
                  Imposta prima dei filtri per salvarli come preset.
                </p>
              )}
            </div>

            {presets.length > 0 && (
              <div className="space-y-1 pt-2 border-t">
                <div className="text-xs font-medium text-muted-foreground mb-1">I tuoi preset</div>
                {presets.map(p => (
                  <div
                    key={p.id}
                    className="flex items-center gap-1 group rounded hover:bg-muted/50 transition-colors"
                  >
                    <button
                      onClick={() => { applyPreset(p.id); setPresetOpen(false); }}
                      className="flex-1 min-w-0 text-left px-2 py-1.5 text-sm"
                      title="Applica preset"
                    >
                      <div className="truncate font-medium">{p.name}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {[
                          p.filters.statuses.length > 0 && `${p.filters.statuses.length} stati`,
                          p.filters.customerIds.length > 0 && `${p.filters.customerIds.length} clienti`,
                          p.filters.stratigraphyName && 'stratigrafia',
                          p.filters.supplier && 'fornitore',
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </button>
                    <button
                      onClick={() => deletePreset(p.id)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-destructive/10 text-destructive transition-opacity"
                      title="Elimina preset"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs gap-1"
            onClick={resetFilters}
          >
            <X className="h-3 w-3" />
            Pulisci tutto
          </Button>
        )}
      </div>

      {/* Pills dei filtri attivi */}
      {hasActiveFilters && (
        <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t">
          {filters.statuses.map(s => {
            const opt = STATUS_OPTIONS.find(o => o.value === s);
            return (
              <FilterPill
                key={`s-${s}`}
                label={`Stato: ${opt?.label ?? s}`}
                onRemove={() => toggleStatus(s)}
              />
            );
          })}
          {filters.customerIds.map(id => (
            <FilterPill
              key={`c-${id}`}
              label={`Cliente: ${customerLabel(id)}`}
              onRemove={() => toggleCustomer(id)}
            />
          ))}
          {filters.stratigraphyName && (
            <FilterPill
              label={`Stratigrafia: ${filters.stratigraphyName}`}
              onRemove={() => setStratigraphy(null)}
            />
          )}
          {filters.supplier && (
            <FilterPill
              label={`Fornitore: ${filters.supplier}`}
              onRemove={() => setSupplier(null)}
            />
          )}
        </div>
      )}
    </div>
  );
};

interface FilterPillProps {
  label: string;
  onRemove: () => void;
}
const FilterPill: React.FC<FilterPillProps> = ({ label, onRemove }) => (
  <button
    onClick={onRemove}
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs hover:bg-primary/20 transition-colors"
    title="Rimuovi filtro"
  >
    {label}
    <X className="h-3 w-3" />
  </button>
);

export default DashboardFilterBar;
